import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  inject,
  DestroyRef,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  Lesson,
  Question,
  EvaluationData,
  Module,
} from 'core/models/course-content.model';
import { CourseEditorService } from 'core/services/course-editor.service';
import { ToastService } from 'core/services/toast.service';
import { ConfirmDialogService } from 'core/services/confirm-dialog.service';
import { QuestionForm } from '../../shared/forms/question-form/question-form';
import { LessonHeader } from 'components/lesson-header/lesson-header';

@Component({
  selector: 'app-evaluation-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, QuestionForm, LessonHeader],
  templateUrl: './evaluation-editor.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full h-full flex flex-col',
  },
})
export class EvaluationEditor implements OnInit {
  @Input() lesson?: Lesson;
  @Input() module?: Module;
  @Input() question?: Question | null;
  @Output() deleteLesson = new EventEmitter<void>();
  @Output() editQuestionRequest = new EventEmitter<Question>();

  private readonly fb = inject(FormBuilder);
  private readonly courseService = inject(CourseEditorService);
  private readonly toastService = inject(ToastService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  protected evaluationForm!: FormGroup;
  protected currentModuleId: string | null = null;
  protected showQuestionForm = signal<boolean>(false);
  protected editingQuestion = signal<Question | null>(null);

  ngOnInit(): void {
    // Se receber module, usar ele diretamente; caso contrário, usar lesson
    if (this.module) {
      this.currentModuleId = this.module.id;
      const questions = this.module.questions || [];
      // Criar o formulário primeiro antes de criar a subscription
      this.evaluationForm = this.fb.group({
        questions: [questions],
      });
      // Observar mudanças no módulo para atualizar questões
      this.courseService.course$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((course) => {
          const updatedModule = course.modules.find(
            (m) => m.id === this.module?.id,
          );
          if (updatedModule && updatedModule.questions && this.evaluationForm) {
            this.evaluationForm.patchValue(
              { questions: updatedModule.questions },
              { emitEvent: false },
            );
          }
        });
      // Observar mudanças na questão atual para atualizar o input
      this.courseService.questionChange$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((questionId) => {
          if (questionId && this.module) {
            const updatedModule = this.courseService
              .getCourse()
              .modules.find((m) => m.id === this.module?.id);
            const updatedQuestion = updatedModule?.questions?.find(
              (q) => q.id === questionId,
            );
            if (updatedQuestion) {
              // Atualizar o input question
              this.question = updatedQuestion;
              this.cdr.markForCheck();
            }
          }
        });
    } else if (this.lesson) {
      const currentModule = this.courseService.getCurrentModule();
      this.currentModuleId = currentModule?.id || null;

      const evaluationData = this.lesson.evaluationData || {
        title: 'Avaliação',
        subtitle: '',
        questions: [],
        totalPoints: 0,
      };

      this.evaluationForm = this.fb.group({
        questions: [evaluationData.questions || []],
      });
    } else {
      // Fallback: buscar módulo atual
      const currentModule = this.courseService.getCurrentModule();
      this.currentModuleId = currentModule?.id || null;
      const questions = currentModule?.questions || [];
      this.evaluationForm = this.fb.group({
        questions: [questions],
      });
    }

    this.updateTotalPoints();
    this.onFormChange();
  }

  protected get questions(): Question[] {
    // Se há uma questão específica sendo exibida, retornar apenas ela
    if (this.question) {
      return [this.question];
    }
    return this.evaluationForm.get('questions')?.value || [];
  }

  protected get currentQuestion(): Question | null {
    return this.question || null;
  }

  protected get totalPoints(): number {
    return this.questions.reduce((sum, q) => sum + (q.points || 0), 0);
  }

  protected get headerTitle(): string {
    // Se há uma questão específica sendo exibida, mostrar o texto dela
    if (this.question) {
      return this.question.text;
    }
    // Se há uma questão sendo editada, mostrar o texto dela
    const editingQ = this.editingQuestion();
    if (editingQ) {
      return editingQ.text;
    }
    // Se há questões, mostrar o texto da primeira
    if (this.questions.length > 0) {
      return this.questions[0].text;
    }
    // Caso contrário, mostrar o título do módulo ou lição
    return this.module?.name || this.lesson?.title || 'Avaliação';
  }

  protected get headerSubtitle(): string {
    // Se há uma questão sendo editada, mostrar o tipo dela
    const editingQ = this.editingQuestion();
    if (editingQ) {
      return this.getQuestionTypeLabel(editingQ.type);
    }
    // Se há questões, mostrar o tipo da primeira
    if (this.questions.length > 0) {
      return this.getQuestionTypeLabel(this.questions[0].type);
    }
    // Caso contrário, mostrar a descrição do módulo ou lição
    return this.module?.description || this.lesson?.description || '';
  }

  private getQuestionTypeLabel(type: Question['type']): string {
    switch (type) {
      case 'single-choice':
        return 'Questão de Seleção Única';
      case 'multiple-choice':
        return 'Questão de Seleção Múltipla';
      case 'boolean':
        return 'Questão Verdadeiro ou Falso';
      default:
        return '';
    }
  }

  protected addQuestion(): void {
    this.editingQuestion.set(null);
    this.showQuestionForm.set(true);
  }

  protected editQuestion(question: Question): void {
    // Se for módulo do tipo evaluation, emitir evento para abrir modal no app.ts
    if (this.module?.contentType === 'evaluations') {
      this.editQuestionRequest.emit(question);
    } else {
      // Modo antigo: editar via formulário interno
      this.editingQuestion.set(question);
      this.showQuestionForm.set(true);
    }
  }

  protected async deleteQuestion(questionId: string): Promise<void> {
    const question = this.questions.find((q) => q.id === questionId);
    const confirmed = await this.confirmDialogService.confirm({
      title: 'Excluir Questão',
      message: `Tem certeza que deseja excluir a questão "${question?.text || 'esta questão'}"?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (confirmed && this.currentModuleId) {
      // Se for módulo do tipo evaluation, usar método do service
      if (this.module?.contentType === 'evaluations') {
        const deleted = this.courseService.deleteQuestion(
          this.currentModuleId,
          questionId,
        );
        if (deleted) {
          this.toastService.success('Questão excluída com sucesso!');
          // Recarregar questões do módulo
          const updatedModule = this.courseService.getCurrentModule();
          if (updatedModule?.questions) {
            this.evaluationForm.patchValue({
              questions: updatedModule.questions,
            });
            this.updateTotalPoints();
          }
        } else {
          this.toastService.error('Erro ao excluir questão. Tente novamente.');
        }
      } else {
        // Modo antigo: atualizar via form
        const questions = this.questions.filter((q) => q.id !== questionId);
        this.evaluationForm.patchValue({ questions });
        this.updateTotalPoints();
        this.onFormChange();
        this.toastService.success('Questão excluída com sucesso!');
      }
    }
  }

  protected onQuestionSave(question: Question): void {
    if (!this.currentModuleId) {
      this.toastService.error(
        'Não foi possível identificar o módulo. Tente novamente.',
      );
      return;
    }

    // Se for módulo do tipo evaluation, usar métodos do service
    if (this.module?.contentType === 'evaluations') {
      const existingIndex = this.questions.findIndex(
        (q) => q.id === question.id,
      );

      if (existingIndex > -1) {
        // Atualizar questão existente
        const updated = this.courseService.updateQuestion(
          this.currentModuleId,
          question.id,
          {
            text: question.text,
            type: question.type,
            points: question.points,
            options: question.options.map((opt) => ({
              label: opt.label,
              text: opt.text,
            })),
            correctAnswers: question.correctAnswers,
          },
        );

        if (updated) {
          this.toastService.success('Questão atualizada com sucesso!');
          // Recarregar questões do módulo e atualizar a questão atual
          const updatedModule = this.courseService.getCurrentModule();
          if (updatedModule?.questions) {
            this.evaluationForm.patchValue({
              questions: updatedModule.questions,
            });
            this.updateTotalPoints();
            // Atualizar a questão atual para refletir as mudanças
            const updatedQuestion = updatedModule.questions.find(
              (q) => q.id === question.id,
            );
            if (updatedQuestion && this.question) {
              // Atualizar o input question para refletir as mudanças
              this.question = updatedQuestion;
              this.cdr.markForCheck();
            }
          }
        } else {
          this.toastService.error(
            'Erro ao atualizar questão. Tente novamente.',
          );
        }
      } else {
        // Criar nova questão
        const created = this.courseService.createQuestion(
          this.currentModuleId,
          {
            text: question.text,
            type: question.type,
            points: question.points,
            options: question.options.map((opt) => ({
              label: opt.label,
              text: opt.text,
            })),
            correctAnswers: question.correctAnswers,
          },
        );

        if (created) {
          this.toastService.success('Questão adicionada com sucesso!');
          // Recarregar questões do módulo
          const updatedModule = this.courseService.getCurrentModule();
          if (updatedModule?.questions) {
            this.evaluationForm.patchValue({
              questions: updatedModule.questions,
            });
            this.updateTotalPoints();
            // Navegar para a questão recém-criada
            this.courseService.goToQuestion(created.id);
            this.cdr.markForCheck();
          }
        } else {
          this.toastService.error('Erro ao criar questão. Tente novamente.');
        }
      }
      // Não fechar o formulário quando salvar - manter em modo de edição
      this.showQuestionForm.set(false);
      this.editingQuestion.set(null);
    } else {
      // Modo antigo: atualizar via form (para lições do tipo evaluation)
      const currentQuestions = this.questions;
      const existingIndex = currentQuestions.findIndex(
        (q) => q.id === question.id,
      );

      let updatedQuestions: Question[];
      if (existingIndex > -1) {
        updatedQuestions = [...currentQuestions];
        updatedQuestions[existingIndex] = question;
        this.toastService.success('Questão atualizada com sucesso!');
      } else {
        updatedQuestions = [...currentQuestions, question];
        this.toastService.success('Questão adicionada com sucesso!');
      }

      this.evaluationForm.patchValue({ questions: updatedQuestions });
      this.updateTotalPoints();
      this.onFormChange();
    }

    this.showQuestionForm.set(false);
    this.editingQuestion.set(null);
  }

  protected onQuestionCancel(): void {
    this.showQuestionForm.set(false);
    this.editingQuestion.set(null);
    // Se for módulo de avaliação com questão selecionada, não fazer nada
    // A questão continuará sendo exibida em modo de edição
  }

  protected onSave(): void {
    if (this.evaluationForm.invalid || this.questions.length === 0) {
      this.evaluationForm.markAllAsTouched();
      if (this.questions.length === 0) {
        this.toastService.error('Adicione pelo menos uma questão à avaliação.');
      } else {
        this.toastService.error(
          'Por favor, preencha todos os campos obrigatórios corretamente.',
        );
      }
      return;
    }

    // Se for módulo do tipo evaluation, não precisa salvar (já está salvo via service)
    if (this.module?.contentType === 'evaluations') {
      this.toastService.success('Avaliação salva com sucesso!');
      return;
    }

    // Modo antigo: salvar via lesson (para lições do tipo evaluation)
    if (!this.lesson) {
      this.toastService.error(
        'Não foi possível identificar a lição. Tente novamente.',
      );
      return;
    }

    // Buscar o módulo atual novamente caso não tenha sido encontrado no ngOnInit
    if (!this.currentModuleId) {
      const currentModule = this.courseService.getCurrentModule();
      this.currentModuleId = currentModule?.id || null;
    }

    if (!this.currentModuleId) {
      this.toastService.error(
        'Não foi possível identificar o módulo da lição. Tente novamente.',
      );
      return;
    }

    const formValue = this.evaluationForm.value;
    const evaluationData: EvaluationData = {
      title: 'Avaliação',
      subtitle: '',
      questions: formValue.questions,
      totalPoints: this.totalPoints,
    };

    const updatedLesson = this.courseService.updateLesson(
      this.currentModuleId,
      this.lesson.id,
      {
        evaluationData,
      },
    );

    if (updatedLesson) {
      this.toastService.success('Avaliação salva com sucesso!');
    } else {
      this.toastService.error('Erro ao salvar avaliação. Tente novamente.');
    }
  }

  private updateTotalPoints(): void {
    // Para módulos do tipo evaluation, não precisa atualizar evaluationData
    if (this.module?.contentType === 'evaluations') {
      return;
    }

    // Modo antigo: atualizar evaluationData da lesson
    if (this.lesson) {
      const total = this.totalPoints;
      const evaluationData = this.lesson.evaluationData || {
        title: 'Avaliação',
        subtitle: '',
        questions: this.questions,
        totalPoints: total,
      };
      evaluationData.totalPoints = total;
    }
  }

  private onFormChange(): void {
    // Para módulos do tipo evaluation, não precisa autosave (já salva via service)
    if (this.module?.contentType === 'evaluations') {
      return;
    }

    // Modo antigo: autosave via lesson
    if (this.evaluationForm.valid && this.currentModuleId && this.lesson) {
      const formValue = this.evaluationForm.value;
      const evaluationData: EvaluationData = {
        title: 'Avaliação',
        subtitle: '',
        questions: formValue.questions,
        totalPoints: this.totalPoints,
      };

      this.courseService.updateLesson(this.currentModuleId, this.lesson.id, {
        evaluationData,
      });
    }
  }

  protected async onDelete(): Promise<void> {
    // Se há uma questão selecionada, excluir apenas a questão
    if (this.question) {
      await this.deleteQuestion(this.question.id);
      return;
    }

    // Caso contrário, excluir o módulo ou lição
    const title = this.module?.name || this.lesson?.title || 'este item';
    let dialogTitle: string;
    if (this.module) {
      dialogTitle = 'Excluir Módulo';
    } else {
      dialogTitle = 'Excluir Lição';
    }

    const confirmed = await this.confirmDialogService.confirm({
      title: dialogTitle,
      message: `Tem certeza que deseja excluir "${title}"?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (confirmed) {
      this.deleteLesson.emit();
    }
  }
}
